<template>
    <div :class="className">
      <label
        v-if="label"
        :htmlFor="name"
        class="block text-body-dark font-semibold text-sm leading-none mb-3 truncate"
      >
        {{ label }}&nbsp;<span v-if="required" class="text-red-500">*</span>
      </label>
      <div class="relative">
        <textarea
          ref="hiddenField"
          :value="modelValue.trim()"
          class="invisible absolute bottom-0 left-0 -z-50 w-full px-4 py-3"
        />
        <textarea
          :id="name"
          ref="inputField"
          :name="name"
          :value="modelValue"
          :placeholder="placeholder"
          :class="[ 'px-4 py-3 flex items-center w-full rounded-lg appearance-none transition duration-300 ease-in-out text-bolder text-sm placeholder-gray-500 focus:outline-none focus:ring-0 overflow-y-hidden min-h-12', shadow ? 'focus:shadow' : '', variantClasses[variant], hasError ? '!border-red-500' : '', inputClassName ]"
          :style="{ height: height === 'auto' ? 'auto' : `${height}px` }"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          :rows="rows"
          v-bind="$attrs"
          @input="onInput"
          @keyup="updateCursorPosition"
          @click="updateCursorPosition"
          @mouseup="updateCursorPosition"
          @keypress="onKeypress"
          @keydown.ctrl.enter.exact.prevent="onSubmit"
          @keydown.meta.enter.exact.prevent="onSubmit"
        />
      </div>
      <p v-if="hasError" class="my-2 text-xs text-red-500">
        {{ error }}
      </p>
    </div>
</template>

<script setup>
defineOptions({
  inheritAttrs: false
});

const variantClasses = {
  normal:
    "bg-gray-100 border border-border-base focus:shadow focus:bg-light focus:border-accent",
  solid:
    "bg-gray-100 border border-border-100 focus:bg-light focus:border-accent",
  outline: "border border-border-base focus:border-accent",
};

const emit = defineEmits(['update:modelValue', 'cursorChanged']);

const props = defineProps({
    modelValue: {
        type: String,
        default: ''
    },
    className: {
        type: String,
        default: ''
    },
    inputClassName: {
        type: String,
        default: ''
    },
    label: {
        type: String,
        default: ''
    },
    name: {
        type: String,
        default: ''
    },
    placeholder: {
        type: String,
        default: ''
    },
    rows: {
        type: Number,
        default: 4
    },
    error: {
        type: String,
        default: ''
    },
    shadow: {
        type: Boolean,
        default: false
    },
    required: {
        type: Boolean,
        default: false
    },
    variant: {
        type: String,
        default: 'normal' // "normal" | "solid" | "outline"
    },
    minHeight: {
      type: Number,
      default: 0
    },
    onSubmit: {
      type: Function,
      default: () => {}
    }
});

const { $eventBus } = useNuxtApp();
const inputField = ref(null);
const hiddenField = ref(null);
const height = ref('auto');
const scrollHeight = ref('auto');
const cursorPosition = ref(0);
const hasError = ref(!!props.error);

watch(() => props.error, (value) => {
  hasError.value = !!value;
});

const onKeypress = () => {
  hasError.value = false;
}

const adjustHeight = () => {
  scrollHeight.value = height.value === 'auto' ? props.minHeight : hiddenField.value.scrollHeight;
  height.value = Math.max(height.value === 'auto' ? props.minHeight : height.value, inputField.value.scrollHeight);
}

const onInput = (event) => {
  adjustHeight();
  emit('update:modelValue', event.target.value);
}

const focusInput = (value) => {
  nextTick(() => {
    if (props.name === value) {
        inputField.value.focus();
    }
  });
};

const focusError = (value) => {
  nextTick(() => {
    if (props.name === value && !!props.error) {
        inputField.value.focus();
        hasError.value = true;
    }
  });
};

const updateCursorPosition = () => {
  cursorPosition.value = inputField.value.selectionStart;
  emit('cursorChanged', cursorPosition.value);
}

watch(() => props.modelValue, (value) => nextTick(() => {
  if (!value) {
    height.value = Math.max(props.minHeight, scrollHeight.value);
  }
}));

onMounted(async () => {
  adjustHeight();
  $eventBus.on('focus:input', focusInput);
  $eventBus.on('focus:error', focusError);
});

onUnmounted(() => {
  $eventBus.off('focus:input', focusInput);
  $eventBus.off('focus:error', focusError);
});

</script>
